import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { useAuth } from '../hooks/useAuth';
import { useIsMobile } from '../hooks/useIsMobile';
import { authState } from '../store/atoms';
import { Teacher, User } from '../types/api';
import { formatPhoneNumber } from '../utils';
import { getWeekdayLabel } from '../utils/formatters/labels';
import { useLanguage } from '../contexts/LanguageContext';
import AccountModals, { AccountModalMode } from './AccountModals';
import { lessonLogAPI } from '../services/lessonLogAPI';


interface MenuItem {
  path: string;
  label: string;
  icon: string;
  count?: number;
  showCount?: boolean;
  countType?: 'default' | 'info'; // default: 핑크, info: 남색
  isLogout?: boolean; // Added for logout menu item
  isUtility?: boolean; // 하단 보조 링크 (이메일/비밀번호 변경)
  pinBottom?: boolean; // 이 항목부터 사이드바 하단에 고정
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  alwaysVisible?: boolean; // 데스크탑에서 항상 표시할지 여부
  pendingCounts?: {
    lessonRequests: number;
    oneDayRequests: number;
    contacts: number;
    modifications: number;
    userCount: number;
  };
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, alwaysVisible = false, pendingCounts }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAtomValue(authState);
  const { language, setLanguage, t } = useLanguage();
  const { logout } = useAuth();
  const isMobile = useIsMobile();

  // 계정 설정 모달 (이메일/비밀번호 변경)
  const [accountModal, setAccountModal] = useState<AccountModalMode>(null);

  // 수업 기록 읽지 않은 댓글 수
  const [lessonLogUnreadCount, setLessonLogUnreadCount] = useState(0);

  // 읽지 않은 수업 기록 수 가져오기 (선생님/학생 공용)
  useEffect(() => {
    const loadLessonLogUnread = async () => {
      if (!auth.isAuthenticated) return;

      try {
        if (auth.userType === 'teacher') {
          const response = await lessonLogAPI.getUnreadCounts();
          if (response.success && response.data) {
            const total = Object.values(response.data).reduce((sum, count) => sum + count, 0);
            setLessonLogUnreadCount(total);
          }
        } else if (auth.userType === 'user') {
          const response = await lessonLogAPI.getMyUnreadCount();
          if (response.success && response.data) {
            setLessonLogUnreadCount(response.data.total);
          }
        }
      } catch {
        // 오류 무시
      }
    };
    loadLessonLogUnread();

    // 읽음 처리 이벤트 리스너
    const handleLessonLogRead = () => loadLessonLogUnread();
    window.addEventListener('lessonLogRead', handleLessonLogRead);

    // 30초마다 갱신
    const interval = setInterval(loadLessonLogUnread, 30000);
    return () => {
      clearInterval(interval);
      window.removeEventListener('lessonLogRead', handleLessonLogRead);
    };
  }, [auth.isAuthenticated, auth.userType, location.pathname]);

  // pendingCounts가 전달되면 사용, 아니면 기본값 사용
  const stats = {
    pendingLessonRequests: pendingCounts?.lessonRequests || 0,
    pendingOneDayRequests: pendingCounts?.oneDayRequests || 0,
    pendingContacts: pendingCounts?.contacts || 0,
    pendingModifications: pendingCounts?.modifications || 0,
    userCount: pendingCounts?.userCount || 0,
  };

  // 사이드바 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.querySelector('.sidebar');
      if (sidebar && !sidebar.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // 통계 데이터는 Header에서 관리하므로 여기서는 로드하지 않음
  // 대신 props로 받거나 전역 상태를 사용해야 함

  const handleMenuClick = async (path: string, isLogout?: boolean) => {
    if (isLogout) {
      handleLogout();
      return;
    }

    // 계정 설정 모달 트리거
    if (path.startsWith('modal:')) {
      setAccountModal(path.replace('modal:', '') as AccountModalMode);
      if (window.innerWidth <= 768) onClose();
      return;
    }

    // 로그인된 사용자의 경우 권한 검증 상태 확인
    if (auth.isAuthenticated && !auth.loading) {
      try {
        // 사이드바를 먼저 닫아서 사용자 경험 개선
        onClose();

        // 네비게이션 실행
        navigate(path);

        // 메뉴 클릭 시 상단으로 스크롤
        setTimeout(() => {
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
        }, 100);
      } catch {
        // 오류 발생 시 홈으로 이동
        navigate('/');
      }
    } else {
      // 인증되지 않은 사용자 또는 로딩 중인 경우
      navigate(path);
      onClose();
    }
  };

  const handleLogout = async () => {
    try {
      // 로그아웃 실행
      await logout();
      onClose(); // 사이드바 닫기
    } catch {
      // 로그아웃 오류 무시
    }
  };

  const handleLanguageChange = (lang: 'KOR' | 'ENG') => {
    setLanguage(lang);
  };

  // 비회원용 메뉴 항목
  const guestMenuItems: MenuItem[] = [
    { path: '/', label: t('home'), icon: '' },
    { path: '/academy-tour', label: t('academy_tour'), icon: '' },
    { path: '/lesson-request', label: t('lesson_request'), icon: '' },
    { path: '/one-day-class', label: t('oneday_class'), icon: '' },
    { path: '/contact', label: t('contact_qna'), icon: '' },
  ];

  // 로그인된 사용자용 메뉴 항목
  const authenticatedMenuItems: MenuItem[] = auth.userType === 'teacher' ? [
    { path: '/admin/dashboard', label: t('dashboard'), icon: '' },
    { path: '/admin/lesson-log', label: '수업 기록', icon: '', count: lessonLogUnreadCount, showCount: lessonLogUnreadCount > 0 },
    { path: '/admin/lesson-schedule', label: t('lesson_schedule_management'), icon: '' },
    {
      path: '/admin/lesson-requests',
      label: t('lesson_request_management'),
      icon: '',
      count: stats.pendingLessonRequests,
      showCount: stats.pendingLessonRequests > 0
    },
    {
      path: '/admin/oneday-requests',
      label: t('oneday_class_management'),
      icon: '',
      count: stats.pendingOneDayRequests,
      showCount: stats.pendingOneDayRequests > 0
    },
    {
      path: '/admin/lesson-modifications',
      label: t('schedule_change_management'),
      icon: '',
      count: stats.pendingModifications,
      showCount: stats.pendingModifications > 0
    },
    { path: '/admin/users', label: t('user_management'), icon: '', count: stats.userCount, showCount: stats.userCount > 0, countType: 'info' },
    {
      path: '/admin/contacts',
      label: t('contact_management'),
      icon: '',
      count: stats.pendingContacts,
      showCount: stats.pendingContacts > 0
    },
    { path: '/admin/practice-booking', label: t('practice_room_management'), icon: '' },
    { path: '/logout', label: t('logout'), icon: '', isLogout: true, pinBottom: true },
  ] : [
    { path: '/user/dashboard', label: '홈', icon: '' },
    { path: '/user/lesson-logs', label: '수업 기록', icon: '', count: lessonLogUnreadCount, showCount: lessonLogUnreadCount > 0 },
    { path: '/user/lesson-schedule', label: '수업일정 변경 신청', icon: '' },
    { path: '/user/practice-booking', label: '연습실 예약', icon: '' },
    { path: 'modal:email', label: '이메일 변경', icon: '', isUtility: true, pinBottom: true },
    { path: 'modal:password', label: '비밀번호 변경', icon: '', isUtility: true },
    { path: '/logout', label: t('logout'), icon: '', isLogout: true },
  ];

  // 모바일에서 선생님 대시보드 메뉴 숨김 (수업일정관리로 리다이렉트되므로)
  const filteredAuthMenuItems = isMobile && auth.userType === 'teacher'
    ? authenticatedMenuItems.filter(item => item.path !== '/admin/dashboard')
    : authenticatedMenuItems;

  const menuItems = auth.isAuthenticated ? filteredAuthMenuItems : guestMenuItems;
  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* 모바일에서 사이드바가 열릴 때만 배경 오버레이 표시 */}
      {isOpen && !alwaysVisible && (
        <div className="sidebar-overlay" onClick={onClose} />
      )}
      <aside className={`sidebar ${isOpen ? 'sidebar-open' : ''} ${alwaysVisible ? 'sidebar-always-visible' : ''} ${auth.isAuthenticated ? 'sidebar-teacher' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            {auth.isAuthenticated ? (
              <h2 className="sidebar-title sidebar-logo">
                DREAM<span className="logo-accent">DRUMS</span>
                <span className="logo-sub">{auth.userType === 'teacher' ? 'TEACHER ADMIN' : 'STUDENT'}</span>
              </h2>
            ) : (
              <h2 className="sidebar-title">Dream Drums</h2>
            )}
          </div>
          <button className="sidebar-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* 로그인된 사용자일 때만 사용자 정보 표시 */}
        {auth.isAuthenticated && auth.userType === 'teacher' && (
          <div className="sidebar-user mobile-only">
            <div className="user-info">
              <div className="user-details">
                <div className="user-name">{(auth.userInfo as Teacher)?.name || auth.userInfo?.name}</div>
                <div className="user-role">
                  {(auth.userInfo as Teacher)?.role === 'admin' ? t('admin') : t('teacher')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 일반 사용자 정보 표시 */}
        {auth.isAuthenticated && auth.userType === 'user' && (
          <div className="sidebar-user-info">
            <div className="user-name-section">
              <div className="user-name-row">
                <span className="user-name">{(auth.userInfo as User)?.name}</span>
                <span className="user-badge">{t('students')}</span>
              </div>
            </div>
            <div className="user-details-list">
              {(auth.userInfo as User)?.email && (
                <div className="user-detail-item">
                  <span className="detail-label">{t('email')}</span>
                  <span className="detail-value">{(auth.userInfo as User)?.email}</span>
                </div>
              )}
              {(auth.userInfo as User)?.phoneNumber && (
                <div className="user-detail-item">
                  <span className="detail-label">{t('phone')}</span>
                  <span className="detail-value">{formatPhoneNumber((auth.userInfo as User)?.phoneNumber || '')}</span>
                </div>
              )}
              {(auth.userInfo as User)?.paymentDayOfMonth && (
                <div className="user-detail-item">
                  <span className="detail-label">{t('payment_day_label')}</span>
                  <span className="detail-value">{t('monthly_on_day', { day: (auth.userInfo as User)?.paymentDayOfMonth ?? 1 })}</span>
                </div>
              )}
              {(auth.userInfo as User)?.lessonDayOfWeek && (
                <div className="user-detail-item">
                  <span className="detail-label">{t('lesson_info_label')}</span>
                  <span className="detail-value">
                    {language === 'ENG'
                      ? (auth.userInfo as User)?.lessonDayOfWeek
                      : getWeekdayLabel((auth.userInfo as User)?.lessonDayOfWeek || '')}
                    {' '}
                    {(auth.userInfo as User)?.lessonStartTime?.slice(0, 5)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 비회원일 때 언어 선택기와 액션 버튼들 */}
        {!auth.isAuthenticated && (
          <div className="sidebar-guest mobile-only">
            <div className="sidebar-language">
              <button
                className={`lang-btn ${language === 'KOR' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('KOR')}
              >
                KOR
              </button>
              <span className="lang-divider">/</span>
              <button
                className={`lang-btn ${language === 'ENG' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('ENG')}
              >
                ENG
              </button>
            </div>
            <div className="guest-actions">
              <a href="/login" className="guest-action-btn login-btn">
                {t('login')}
              </a>
            </div>
          </div>
        )}

        {/* 네비게이션 메뉴 */}
        <nav className="sidebar-nav">
          {menuItems.map((item, index) => (
            <div key={index} className={`nav-item ${item.pinBottom ? 'nav-item-pin' : ''}`}>
              <button
                className={`nav-link ${isActive(item.path) ? 'nav-link-active' : ''} ${item.isLogout ? 'nav-link-logout' : ''} ${item.isUtility ? 'nav-link-utility' : ''}`}
                onClick={() => handleMenuClick(item.path, item.isLogout)}
              >
                <span className="nav-label">{item.label}</span>
                {item.showCount && item.count && item.count > 0 && (
                  <span className={`nav-count ${item.countType === 'info' ? 'nav-count-info' : ''}`}>{item.count}</span>
                )}
              </button>
            </div>
          ))}
        </nav>

        {/* 사이드바 푸터 */}
        <div className="sidebar-footer">
          <div className="footer-content">
            <p className="footer-text">Dream Drums</p>
            <p className="footer-subtext">Professional Drum Education</p>
          </div>
        </div>
      </aside>

      {/* 계정 설정 모달 (이메일/비밀번호 변경) */}
      <AccountModals mode={accountModal} onClose={() => setAccountModal(null)} />
    </>
  );
};

export default Sidebar; 